import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { BRAND } from '../lib/brand.ts';
import { AuthSplitLayout, AuthInfoChip } from '../components/AuthSplitLayout.tsx';

export function CompanyLogin() {
  const { session } = useCompanyAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (session) navigate('/c', { replace: true });
  }, [session, navigate]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await publicApi.post('/api/auth/company/request-link', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthSplitLayout
      variant="vendor"
      formFooter={
        <>
          Need help?{' '}
          <a
            href={`mailto:${BRAND.supportEmail}`}
            className="font-semibold text-brand-primary hover:underline"
          >
            Email {BRAND.supportEmail}
          </a>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500">
            Enter your work email and we’ll send you a secure sign-in link — no password needed.
          </p>
        </div>

        {sent ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            If that email matches an account, we’ve sent a sign-in link. It’s valid for 2 days —
            check your inbox to sign in.
          </div>
        ) : (
          <form className="flex flex-col gap-5" onSubmit={onSubmit}>
            <Field label="Work email" required>
              <TextInput
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                style={{ borderRadius: 10 }}
              />
            </Field>
            <ErrorMessage error={error} />
            <Btn
              type="submit"
              loading={submitting}
              disabled={!email.trim()}
              className="w-full"
              style={{ backgroundColor: 'var(--brand-orange)', borderRadius: 10, height: 48 }}
            >
              Email me a sign-in link
            </Btn>
            <AuthInfoChip tone="purple">
              Sign-in links are valid for 2 days and work on any device.
            </AuthInfoChip>
          </form>
        )}
      </div>
    </AuthSplitLayout>
  );
}
